import { z } from "zod";
import { ZDate } from "./ZDate.js";
import { PlantPlotSignal } from "./admin.js";
import { BwVesselTrackData } from "./barentsWatch.js";
import { zArr, zNullable, zNum, zObj, zStr } from "./zodWrapper.js";
import { heatMapTypes } from "./heatMapTypes.js";
export var FuelPageTargetType;
(function (FuelPageTargetType) {
    FuelPageTargetType["TOTAL_SPENT"] = "total_spent";
    FuelPageTargetType["PERCENTAGE_TIME_ALLOCATION"] = "percentage_time_allocation";
    FuelPageTargetType["AVG_ENGINES_IN_USE"] = "avg_engines_in_use";
    FuelPageTargetType["AVG_LOAD_ENGINE_IN_USE"] = "avg_load_engine_in_use";
})(FuelPageTargetType || (FuelPageTargetType = {}));
export const FuelPageTargetTypeEnum = z.nativeEnum(FuelPageTargetType);
export const FuelPagePerformanceFuelArgs = zObj({
    plantId: zNum(),
    baselineYear: zNum(),
    t1: ZDate,
    t2: ZDate,
});
export const FuelPagePerformanceArgs = zObj({
    plantId: zNum(),
    t1: ZDate,
    t2: ZDate,
});
export const Fuel = z.object({
    mdo: zNum(),
    gas: zNum(),
    lvoc: zNum(),
    svoc: zNum(),
});
export const FuelPageModePerformanceFuel = zObj({
    opModeId: zNullable(zNum()),
    baselineTonnesPerDay: zNullable(zNum()),
    actualTonnesPerDay: Fuel,
});
export const FuelPageModePerformance = zObj({
    opModeId: zNullable(zNum()),
    value: zNum(),
});
export const EngineRunningPerOpMode = zObj({
    opModeId: zNum(),
    avgEnginesRunning: zNum(),
});
export const SaveTargetArgs = zObj({
    plantId: zNum(),
    targetType: FuelPageTargetTypeEnum,
    targets: zArr(zObj({
        opModeId: zNum(),
        newTarget: zNum(),
    })),
});
export const FuelSavingTargets = zArr(zObj({
    opModeId: zNullable(zNum()),
    target: zNum(),
}));
export const FuelSavingGetTargetsArgs = zObj({
    plantId: zNum(),
    targetType: FuelPageTargetTypeEnum,
});
export const PowerConsumerGroup = zObj({
    id: zStr(),
    name: zStr(),
});
export const FuelPageGetProducersOrConsumersArgs = zObj({
    plantId: zNum(),
});
export const FuelPagePowerConsumer = zObj({
    sensorId: zNum(),
    name: zStr(),
    powerConsumerGroupId: zStr(),
    maxPower: zNullable(zNum()),
});
export const FuelPageFuelConsumer = zObj({
    sensorId: zNum(),
    name: zStr(),
});
export const FuelPagePowerProducer = zObj({
    sensorId: zNum(),
    name: zStr(),
    maxPower: zNum(),
});
export const PlantOpModeIntervalArgs = zObj({
    plantId: zNum(),
    opModeId: zNum(),
    t1: ZDate,
    t2: ZDate,
});
export var FuelPageConsumersTargetType;
(function (FuelPageConsumersTargetType) {
    FuelPageConsumersTargetType["PERCENTAGE_POWER_IN_MODE"] = "percentage_power_in_mode";
    FuelPageConsumersTargetType["AVG_POWER_IN_MODE"] = "avg_power_in_mode";
    FuelPageConsumersTargetType["PERCENTAGE_FUEL_IN_MODE"] = "percentage_fuel_in_mode";
    FuelPageConsumersTargetType["PERCENTAGE_LOAD_IN_MODE"] = "percentage_load_in_mode";
})(FuelPageConsumersTargetType || (FuelPageConsumersTargetType = {}));
export const FuelPageConsumersTargetTypeEnum = z.nativeEnum(FuelPageConsumersTargetType);
export const FuelConsumersTargets = zArr(zObj({
    consumerId: zNum(),
    target: zNum(),
}));
export const FuelConsumersGetTargetsArgs = zObj({
    plantId: zNum(),
    targetType: FuelPageConsumersTargetTypeEnum,
    opModeId: zNum(),
});
export const SaveFuelConsumersTargetArgs = zObj({
    targetType: FuelPageConsumersTargetTypeEnum,
    targets: zArr(zObj({
        consumerId: zNum(),
        opModeId: zNum(),
        newTarget: zNum(),
    })),
});
export const FuelPageConsumersTargets = zArr(zObj({
    consumerId: zNum(),
    target: zNum(),
}));
export var GroupedByType;
(function (GroupedByType) {
    GroupedByType["MONTH"] = "month";
    GroupedByType["DAY"] = "day";
})(GroupedByType || (GroupedByType = {}));
export const GroupedByTypeEnum = z.nativeEnum(GroupedByType);
export const FuelPageTotalArgs = zObj({
    plantId: zNum(),
    t1: ZDate,
    t2: ZDate,
    groupedBy: GroupedByTypeEnum,
});
export const TotalData = zObj({
    ref: ZDate,
    tonnes: zNum(),
});
export const FuelPageTotal = zObj({
    data: zArr(TotalData),
    co2Multiplier: zNum(),
});
export const FuelPageEventsArgs = zObj({
    opModeId: zArr(zNum()),
    plantId: zNum(),
    interval: z.object({
        t1: ZDate,
        t2: ZDate,
    }),
});
export const FuelEventsMetaData = zObj({
    conditionSensorsId: zArr(zNum()),
    weatherSensorsId: zArr(zNum()), // TODO: don't use this property, use weather sensor ids endpoint
    fuelConsumersSensorsId: zArr(zNum()),
    loadSensorsId: zArr(zNum()),
    opModeId: zNum(),
    powerProducers: zArr(z.object({
        kwSensorId: z.number(),
        maxKw: z.nullable(z.number()),
    })),
});
export const FuelEvent = zObj({
    t1: ZDate,
    t2: ZDate,
    mainEngineFuelM3Baseline: zNullable(zNum()),
    mainEngineFuelM3: zNum(),
    plantTotalFuelM3: zNum(),
});
export const FuelPageOpModeEventData = zObj({
    events: zArr(FuelEvent),
    metaData: FuelEventsMetaData,
});
export const FuelPageGetVesselTrackArgs = zObj({
    plantId: zNum(),
    t1: ZDate,
    t2: ZDate,
});
export const FuelPageVesselTrack = zObj({
    track: zNullable(BwVesselTrackData),
});
export const FuelPageConsumerKw = zObj({
    sensorId: zNum(),
    kw: zNum(),
});
export const FuelPagePlantTZ = zObj({
    plants: zArr(zObj({
        id: zNum(),
    })),
});
export const FuelPageGetPlantTZArgs = zObj({
    plants: zArr(zNum()),
});
export const FuelPageGetOpModePlotsArgs = zObj({
    opModeId: zNum(),
});
export const FuelPageRemoveOpModePlotArgs = zObj({
    opModeId: zNum(),
});
export const FuelPageResetPlotsArgs = zObj({ opModeId: zNum() });
export const FuelPageLoadOnMainEngines = zObj({
    opModeId: zNullable(zNum()),
    averageLoadPercent: zNum(),
});
export const FuelPageMainEnginesInUse = zObj({
    opModeId: zNullable(zNum()),
    noOfEngines: zNum(),
});
export const FuelPagePercentageProp = zObj({
    sensorId: zNum(),
    percentage: zNum(),
});
export const FuelPageAddOpModePlot = zObj({
    id: zNum(),
});
export const FuelPageAddOpModePlotArgs = zObj({
    plotName: zStr(),
    opModeId: zNum(),
    signals: zArr(PlantPlotSignal),
});
export const FuelPagePowerConsumed = z.object({
    powerProducedKWH: zNum(),
    powerConsumers: zArr(zObj({
        sensorId: zNum(),
        consumedKWH: zNum(),
    })),
});
export const FuelPageGetBaselineDataArgs = z.object({
    plantId: z.number(),
    t1: ZDate,
    t2: ZDate,
});
export const FuelPageGetBaselineDataResponse = z.object({
    samples: z.array(z.object({
        timestamp: ZDate,
        fuelRateLitresPerHour: z.object({
            baselineValue: z.optional(z.number()),
            actualValue: z.number(),
        }),
        powerProductionPercentage: z.number(),
    })),
});
export const FuelEfficiencyRequestSchema = z.object({
    plantId: z.number(),
    interval: z.object({
        gte: ZDate,
        lt: ZDate,
    }),
});
export const FuelEfficiencyResponseSchema = z.array(z.tuple([ZDate, z.number(), z.number()]));
export const GetHydrodynamicPerformanceArgs = z.object({
    plantId: z.number(),
    mode: z.enum(heatMapTypes),
    gte: ZDate,
    lt: ZDate,
});
export const Axis = z.object({ min: z.number(), max: z.number() });
export const Heatmap = z.object({
    yAxis: Axis,
    speedAxis: Axis,
    /**
     * a matrix containing the cell values of the heatmap
     * the matrix can be accessed like this: grid[x][y].
     * example: (grid is 100x100)
     * to get the top left cell, use grid[0][99]
     * top right: grid[99][99]
     * bottom right: grid[99][0]
     * bottom left: grid[0][0]
     */
    grid: z.array(z.array(z.number())),
    coefficients: z.object({
        a: z.number(),
        b: z.number(),
        c: z.number(),
    }),
    avgSpeed: z.number(),
    durationHours: z.number(),
});
export const GetHydrodynamicPerformanceResponse = z.object({
    heatMaps: z.array(Heatmap.extend({ timestamp: ZDate })),
});
export const GetPlantMaxKwArgs = z.object({ plantIds: z.array(z.number()) });
export const GetPlantMaxKwResponse = z.array(z.object({ plantId: z.number(), maxKw: z.number() }));
export const TonnesGroupedByOpModeAndDateRefArgs = z.object({
    plantId: z.number(),
    interval: z.object({
        gte: ZDate,
        lt: ZDate,
    }),
    groupBy: z.enum(["month", "day"]),
});
export const TonnesGroupedByOpModeAndDateRefResponse = z.object({
    data: z.array(z.object({
        ref: ZDate,
        opModeEvents: z.array(z.object({
            opModeId: z.nullable(z.number()),
            tonnesMdo: z.number(),
            tonnesGas: z.number(),
            tonnesLvoc: z.number(),
            tonnesSvoc: z.number(),
        })),
    })),
});
export const TrimPerformanceGetHeatmapsArgs = z.object({
    plantId: z.number(),
    opModeId: z.number(),
    interval: z.object({
        gte: ZDate,
        lt: ZDate,
    }),
});
export const TrimPerformanceGetHeatmapsResponse = z.object({
    heatMaps: z.array(Heatmap.extend({ timestamp: ZDate, optimumTrim: z.number() })),
});
//# sourceMappingURL=fuel.js.map